import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
} from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import ImportFileSection from "./ImportFileSection";
import FileList from "./FileList";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { BulkResult, useAccountState } from "pages/Accounts/accountContext";
import { Action, State } from "../reducer";

type Params = {
  dispatch: React.Dispatch<Action>;
  state: State;
};

type DeletePreview = {
  toDelete: string[];
  ignored: BulkResult["errors"];
};

export default function ImportView({ dispatch, state }: Params) {
  const { t } = useTranslation("accounts");
  const { actions } = useAccountState();
  const { enqueueSnackbar } = useSnackbar();

  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState<DeletePreview>(null);
  const [tab, setTab] = useState<"toDelete" | "ignored">("toDelete");
  const isDelete = state.action === "DELETE";

  const canSave = state.loadedFiles?.some(f => f.items.length > 0);

  // On delete, uploading a file goes straight to the dry run
  const previewOnLoad = useRef(false);
  useEffect(() => {
    if (!previewOnLoad.current) return;
    previewOnLoad.current = false;
    if (canSave) previewDelete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loadedFiles]);

  // Files can be added while a dry run is in flight: only the last run counts
  const lastRun = useRef(0);
  async function previewDelete() {
    const run = ++lastRun.current;
    // The same email can come twice (in a file or across files)
    const emails = [
      ...new Map(
        state.loadedFiles
          .flatMap(f => f.items)
          .map(i => [i.account.email.toLowerCase(), i.account.email])
      ).values(),
    ];

    setChecking(true);
    try {
      const ignored: DeletePreview["ignored"] = [];
      for (let i = 0; i < emails.length; i += 100) {
        const { errors } = await actions.bulkDelete(
          emails.slice(i, i + 100),
          true
        );
        ignored.push(...errors);
      }

      if (run !== lastRun.current) return;
      const skip = new Set(ignored.map(a => a.email));
      setTab("toDelete");
      setPreview({ toDelete: emails.filter(e => !skip.has(e)), ignored });
    } catch (error) {
      console.error(error);
      if (run === lastRun.current) {
        enqueueSnackbar(t("error.unknownError"), { variant: "error" });
      }
    } finally {
      if (run === lastRun.current) setChecking(false);
    }
  }

  return (
    <>
      <DialogContent>
        <ImportFileSection
          action={state.action}
          onImport={files => {
            previewOnLoad.current = isDelete;
            dispatch({ type: "ADD_FILES", files });
          }}
        />
        <FileList
          files={state.loadedFiles}
          onFileRemove={index => dispatch({ type: "REMOVE_FILE", index })}
          onShowAccounts={file => dispatch({ type: "SHOW_ACCOUNTS", file })}
          onShowFormatErrors={file =>
            dispatch({ type: "SHOW_FORMAT_ERRORS", file })
          }
        />
      </DialogContent>
      <DialogActions>
        {canSave && (
          <Button
            onClick={() =>
              isDelete ? previewDelete() : dispatch({ type: "SAVING_ACCOUNTS" })
            }
            disabled={checking}
            startIcon={checking && <CircularProgress size={16} />}
            color="primary"
            variant="contained"
            disableElevation
          >
            {isDelete
              ? t("modal.confirmDelete.review")
              : t(`modal.save.${state.action}`)}
          </Button>
        )}
      </DialogActions>
      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        scroll="paper"
        fullWidth
      >
        <DialogTitle>
          {t("modal.confirmDelete.title", {
            count: preview?.toDelete.length,
          })}
        </DialogTitle>
        {/* Outside DialogContent so the tabs don't scroll with the list */}
        <Box px={3}>
          <DialogContentText>{t("modal.confirmDelete.text")}</DialogContentText>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              value="toDelete"
              label={t("modal.confirmDelete.tabs.toDelete", {
                count: preview?.toDelete.length,
              })}
            />
            <Tab
              value="ignored"
              label={t("modal.confirmDelete.tabs.ignored", {
                count: preview?.ignored.length,
              })}
              disabled={!preview?.ignored.length}
            />
          </Tabs>
        </Box>
        <DialogContent dividers>
          <List dense>
            {tab === "toDelete" &&
              preview?.toDelete.map(email => (
                <ListItem key={email} disableGutters>
                  <ListItemText primary={email} />
                </ListItem>
              ))}
            {tab === "ignored" &&
              preview?.ignored.map(({ email, error }) => (
                <ListItem key={email} disableGutters>
                  <ListItemText
                    primary={email}
                    secondary={error}
                    secondaryTypographyProps={{ color: "error" }}
                  />
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setPreview(null)}>
            {t("importAccountsModal.goBackBtn")}
          </Button>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={!preview?.toDelete.length}
            onClick={() =>
              dispatch({
                type: "SAVING_ACCOUNTS",
                emailsToDelete: preview.toDelete,
              })
            }
          >
            {t("modal.save.DELETE")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
